<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type {
  PopupDisplayAnimation,
  PopupEmbedSettings,
  PopupScreenPosition,
  PopupSizePreset,
} from "@aswin.dev/types";
import { POPUP_SIZE_PRESET_WIDTH } from "@aswin.dev/types";
import { useI18n } from "../composables/useI18n";
import { EDITOR_KEY } from "../keys";
import type { BaseEditorReturn } from "../composables/useEditorCore";
import { resolvePopupEmbed } from "../utils/resolvePopupEmbed";
import PopupDesignSwitch from "./PopupDesignSwitch.vue";
import PopupOverlayImagePanel from "./PopupOverlayImagePanel.vue";

const props = withDefaults(
  defineProps<{ layout?: "standalone" | "popupAdjacent" }>(),
  { layout: "standalone" },
);

const editor = inject(EDITOR_KEY) as BaseEditorReturn | null;
if (!editor) {
  throw new Error("PopupDesignView requires EDITOR_KEY");
}

const { t } = useI18n();

const rootClass = computed(() =>
  props.layout === "popupAdjacent"
    ? "tpl-popup-design tpl:w-full tpl:overflow-x-hidden tpl:px-4 tpl:pb-6 tpl:pt-4 tpl:bg-[var(--tpl-bg)]"
    : "tpl-popup-design tpl:mx-auto tpl:max-w-md tpl:px-5 tpl:py-8 tpl:bg-[var(--tpl-bg)]",
);

const presetGridClass = computed(() =>
  props.layout === "popupAdjacent"
    ? "tpl:mb-8 tpl:grid tpl:grid-cols-3 tpl:gap-2"
    : "tpl:mb-8 tpl:grid tpl:grid-cols-3 tpl:gap-3 sm:tpl:grid-cols-6",
);

const designTab = ref<"style" | "position" | "closing" | "css">("style");

const designTabs = computed(() => [
  { id: "style" as const, label: t.popupDesign.tabStyle },
  { id: "position" as const, label: t.popupDesign.tabPosition },
  { id: "closing" as const, label: t.popupDesign.tabClosing },
  { id: "css" as const, label: t.popupDesign.tabCss },
]);

const popup = computed(() => resolvePopupEmbed(editor!.content.value.settings));

function patchDesign(patch: Partial<PopupEmbedSettings["design"]>): void {
  const cur = popup.value;
  const design = { ...cur.design, ...patch };
  const updates: Record<string, unknown> = {
    popup: { ...cur, design },
  };
  if (patch.sizePreset !== undefined) {
    updates.width = POPUP_SIZE_PRESET_WIDTH[patch.sizePreset];
  }
  editor!.updateSettings(
    updates as Parameters<BaseEditorReturn["updateSettings"]>[0],
  );
}

function toggleDesign<K extends keyof PopupEmbedSettings["design"]>(
  key: K,
  value: PopupEmbedSettings["design"][K],
): void {
  patchDesign({ [key]: value } as Partial<PopupEmbedSettings["design"]>);
}

function toggleOverlayImage(): void {
  const next = !popup.value.design.overlayImage;
  patchDesign({
    overlayImage: next,
    ...(!next ? { overlayImageUrl: "" } : {}),
  });
}

function setUseOverlay(enabled: boolean): void {
  if (!enabled) {
    patchDesign({
      useOverlay: false,
      overlayImage: false,
      overlayImageUrl: "",
    });
  } else {
    patchDesign({ useOverlay: true });
  }
}

function setOverlayImageUrl(url: string): void {
  patchDesign({ overlayImageUrl: url });
}

function toggleBackgroundImage(): void {
  const next = !popup.value.design.backgroundImage;
  patchDesign({
    backgroundImage: next,
    // Drop the stored URL when the user turns the feature off so re-enabling
    // surfaces the empty picker instead of a stale image.
    ...(!next ? { backgroundImageUrl: "" } : {}),
  });
}

function setBackgroundImageUrl(url: string, templateWidth?: number): void {
  patchDesign({ backgroundImageUrl: url });
  if (templateWidth !== undefined) {
    editor!.updateSettings({ width: templateWidth });
  }
}

function onBackgroundImageFitChange(e: Event): void {
  patchDesign({
    backgroundImageFit: (e.target as HTMLSelectElement).value as
      | "auto"
      | "cover"
      | "contain",
  });
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

const OVERLAY_BLUR_MIN = 0;
const OVERLAY_BLUR_MAX = 30;

function clampBlur(n: number): number {
  if (!Number.isFinite(n)) return OVERLAY_BLUR_MIN;
  return Math.min(OVERLAY_BLUR_MAX, Math.max(OVERLAY_BLUR_MIN, n));
}

function onOverlayBlurInput(e: Event): void {
  const raw = Number((e.target as HTMLInputElement).value);
  patchDesign({ overlayBlur: clampBlur(raw) });
}

function onOverlayImageOpacityInput(e: Event): void {
  const raw = Number((e.target as HTMLInputElement).value);
  patchDesign({ overlayImageOpacity: clamp01(raw / 100) });
}

const POSITION_GRID: { id: PopupScreenPosition; cellAlign: string }[] = [
  {
    id: "topLeft",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-start tpl:items-start",
  },
  {
    id: "topCenter",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-center tpl:items-start",
  },
  {
    id: "topRight",
    cellAlign: "tpl:flex tpl:h-full tpl:w-full tpl:justify-end tpl:items-start",
  },
  {
    id: "middleLeft",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-start tpl:items-center",
  },
  {
    id: "center",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-center tpl:items-center",
  },
  {
    id: "middleRight",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-end tpl:items-center",
  },
  {
    id: "bottomLeft",
    cellAlign: "tpl:flex tpl:h-full tpl:w-full tpl:justify-start tpl:items-end",
  },
  {
    id: "bottomCenter",
    cellAlign:
      "tpl:flex tpl:h-full tpl:w-full tpl:justify-center tpl:items-end",
  },
  {
    id: "bottomRight",
    cellAlign: "tpl:flex tpl:h-full tpl:w-full tpl:justify-end tpl:items-end",
  },
];

const animationSelectOptions = computed(() => [
  { id: "smooth" as const, label: t.popupDesign.animationSmooth },
  { id: "fadeIn" as const, label: t.popupDesign.animationFadeIn },
  { id: "slideIn" as const, label: t.popupDesign.animationSlideIn },
  { id: "none" as const, label: t.popupDesign.animationNone },
]);

function onAnimationChange(e: Event): void {
  patchDesign({
    displayAnimation: (e.target as HTMLSelectElement)
      .value as PopupDisplayAnimation,
  });
}

function onCloseButtonPositionChange(e: Event): void {
  patchDesign({
    closeButtonPosition: (e.target as HTMLSelectElement).value as
      | "left"
      | "right",
  });
}

const sizePresets = computed(() =>
  (
    [
      "small",
      "medium",
      "large",
      "fullscreen",
      "fullHeight",
      "custom",
    ] as PopupSizePreset[]
  ).map((id) => ({
    id,
    label: t.popupDesign.sizeLabels[id],
  })),
);
</script>

<template>
  <div :class="rootClass">
    <div
      class="tpl:mb-6 tpl:flex tpl:flex-wrap tpl:gap-1 tpl:rounded-lg tpl:bg-[var(--tpl-bg-elevated)] tpl:p-1"
    >
      <button
        v-for="tab in designTabs"
        :key="tab.id"
        type="button"
        class="tpl:rounded-md tpl:border-none tpl:px-3 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:transition-colors"
        :class="
          designTab === tab.id
            ? 'tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text)] tpl:shadow-sm'
            : 'tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]'
        "
        @click="designTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <template v-if="designTab === 'style'">
      <p
        class="tpl:mb-4 tpl:text-[11px] tpl:font-semibold tpl:uppercase tpl:tracking-wide tpl:text-[var(--tpl-text-muted)]"
      >
        {{ t.popupDesign.standardSize }}
      </p>
      <div :class="presetGridClass">
        <button
          v-for="preset in sizePresets"
          :key="preset.id"
          type="button"
          class="tpl:flex tpl:flex-col tpl:items-center tpl:gap-2 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-3 tpl:text-center tpl:transition-shadow hover:tpl:border-[var(--tpl-primary)]"
          :class="
            popup.design.sizePreset === preset.id
              ? 'tpl:ring-2 tpl:ring-[var(--tpl-text)] tpl:ring-offset-2 tpl:ring-offset-[var(--tpl-bg)]'
              : ''
          "
          @click="patchDesign({ sizePreset: preset.id })"
        >
          <span
            class="tpl:flex tpl:h-12 tpl:w-full tpl:items-center tpl:justify-center tpl:rounded-md tpl:bg-[var(--tpl-bg-elevated)]"
          >
            <span
              class="tpl:block tpl:rounded tpl:bg-[var(--tpl-text-muted)] tpl:opacity-80"
              :class="{
                'tpl:h-6 tpl:w-10': preset.id === 'small',
                'tpl:h-8 tpl:w-12': preset.id === 'medium',
                'tpl:h-10 tpl:w-14': preset.id === 'large',
                'tpl:h-12 tpl:w-full': preset.id === 'fullscreen',
                'tpl:h-12 tpl:w-8': preset.id === 'fullHeight',
                'tpl:h-8 tpl:w-10 tpl:border tpl:border-dashed tpl:border-[var(--tpl-border)] tpl:bg-transparent':
                  preset.id === 'custom',
              }"
            />
          </span>
          <span
            class="tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text)]"
            >{{ preset.label }}</span
          >
        </button>
      </div>

      <p
        class="tpl:mb-3 tpl:text-[11px] tpl:font-semibold tpl:uppercase tpl:tracking-wide tpl:text-[var(--tpl-text-muted)]"
      >
        {{ t.popupDesign.options }}
      </p>
      <div
        class="tpl:mb-8 tpl:space-y-5 tpl:rounded-[10px] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:px-4 tpl:py-5"
      >
        <div
          class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
        >
          <span
            class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
            >{{ t.popupDesign.useOverlay }}</span
          >
          <PopupDesignSwitch
            :checked="popup.design.useOverlay"
            @toggle="setUseOverlay(!popup.design.useOverlay)"
          />
        </div>
        <template v-if="popup.design.useOverlay">
          <div
            class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
          >
            <span
              class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
              >{{ t.popupDesign.overlayImage }}</span
            >
            <PopupDesignSwitch
              :checked="popup.design.overlayImage"
              @toggle="toggleOverlayImage()"
            />
          </div>

          <PopupOverlayImagePanel
            v-if="popup.design.overlayImage"
            :image-url="popup.design.overlayImageUrl ?? ''"
            @set-url="setOverlayImageUrl"
          />

          <div
            v-if="
              popup.design.overlayImage &&
              (popup.design.overlayImageUrl ?? '').length > 0
            "
            class="tpl:space-y-2"
          >
            <div class="tpl:flex tpl:items-center tpl:justify-between">
              <label
                class="tpl:block tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
                for="popup-overlay-image-opacity"
              >
                {{ t.popupDesign.overlayImageOpacity }}
              </label>
              <span
                class="tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:tabular-nums"
              >
                {{ Math.round((popup.design.overlayImageOpacity ?? 1) * 100) }}%
              </span>
            </div>
            <input
              id="popup-overlay-image-opacity"
              type="range"
              class="tpl:w-full tpl:accent-[var(--tpl-primary)]"
              min="0"
              max="100"
              step="5"
              :value="Math.round((popup.design.overlayImageOpacity ?? 1) * 100)"
              @input="onOverlayImageOpacityInput"
            />
          </div>

          <div class="tpl:space-y-2">
            <div class="tpl:flex tpl:items-center tpl:justify-between">
              <label
                class="tpl:block tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
                for="popup-overlay-blur"
              >
                {{ t.popupDesign.overlayBlur }}
              </label>
              <span
                class="tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:tabular-nums"
              >
                {{ Math.round(popup.design.overlayBlur ?? 8) }}px
              </span>
            </div>
            <input
              id="popup-overlay-blur"
              type="range"
              class="tpl:w-full tpl:accent-[var(--tpl-primary)]"
              :min="0"
              :max="30"
              step="1"
              :value="Math.round(popup.design.overlayBlur ?? 8)"
              @input="onOverlayBlurInput"
            />
            <p class="tpl:m-0 tpl:text-[11px] tpl:text-[var(--tpl-text-muted)]">
              {{ t.popupDesign.overlayBlurHint }}
            </p>
          </div>
        </template>
        <div
          class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
        >
          <span
            class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
            >{{ t.popupDesign.backgroundImage }}</span
          >
          <PopupDesignSwitch
            :checked="popup.design.backgroundImage"
            @toggle="toggleBackgroundImage()"
          />
        </div>

        <template v-if="popup.design.backgroundImage">
          <PopupOverlayImagePanel
            :image-url="popup.design.backgroundImageUrl ?? ''"
            @set-url="setBackgroundImageUrl"
          />

          <div
            v-if="(popup.design.backgroundImageUrl ?? '').length > 0"
            class="tpl:space-y-2"
          >
            <label
              class="tpl:block tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
              for="popup-background-image-fit"
            >
              {{ t.popupDesign.backgroundImageFitHeading }}
            </label>
            <select
              id="popup-background-image-fit"
              class="tpl:w-full tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2.5 tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
              :value="popup.design.backgroundImageFit ?? 'cover'"
              @change="onBackgroundImageFitChange"
            >
              <option value="auto">{{ t.popupDesign.fitAuto }}</option>
              <option value="cover">{{ t.popupDesign.fitCover }}</option>
              <option value="contain">{{ t.popupDesign.fitContain }}</option>
            </select>
          </div>

          <div
            v-if="(popup.design.backgroundImageUrl ?? '').length > 0"
            class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
          >
            <span
              class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
              >{{ t.popupDesign.backgroundImageOnly }}</span
            >
            <PopupDesignSwitch
              :checked="popup.design.backgroundImageOnly ?? false"
              @toggle="
                toggleDesign(
                  'backgroundImageOnly',
                  !(popup.design.backgroundImageOnly ?? false),
                )
              "
            />
          </div>
        </template>
      </div>

      <!-- <div
        class="tpl:mb-8 tpl:rounded-[10px] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:px-4 tpl:py-5"
      >
        <div
          class="tpl:mb-3 tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
        >
          <span
            class="tpl:text-sm tpl:font-medium tpl:leading-snug tpl:text-[var(--tpl-text)]"
          >
            {{ t.popupDesign.multiColumn }}
          </span>
          <PopupDesignSwitch
            :checked="popup.design.multiColumnEnabled"
            @toggle="
              toggleDesign(
                'multiColumnEnabled',
                !popup.design.multiColumnEnabled,
              )
            "
          />
        </div>
        <div v-if="popup.design.multiColumnEnabled" class="tpl:flex tpl:gap-3">
          <button
            type="button"
            class="tpl:flex-1 tpl:rounded-lg tpl:border tpl:px-3 tpl:py-4"
            :class="
              popup.design.multiColumnPreset === 'two'
                ? 'tpl:border-[var(--tpl-text)] tpl:bg-[var(--tpl-bg-elevated)]'
                : 'tpl:border-[var(--tpl-border)]'
            "
            @click="patchDesign({ multiColumnPreset: 'two' })"
          >
            <span class="tpl:flex tpl:justify-center tpl:gap-0.5">
              <span
                class="tpl:h-10 tpl:flex-1 tpl:rounded tpl:bg-[var(--tpl-text-muted)] tpl:opacity-80"
              />
              <span
                class="tpl:h-10 tpl:flex-1 tpl:rounded tpl:bg-[var(--tpl-text-muted)] tpl:opacity-80"
              />
            </span>
          </button>
          <button
            type="button"
            class="tpl:flex-1 tpl:rounded-lg tpl:border tpl:px-3 tpl:py-4"
            :class="
              popup.design.multiColumnPreset === 'three'
                ? 'tpl:border-[var(--tpl-text)] tpl:bg-[var(--tpl-bg-elevated)]'
                : 'tpl:border-[var(--tpl-border)]'
            "
            @click="patchDesign({ multiColumnPreset: 'three' })"
          >
            <span class="tpl:flex tpl:justify-center tpl:gap-0.5">
              <span
                class="tpl:h-10 tpl:flex-1 tpl:rounded tpl:bg-[var(--tpl-bg-hover)]"
              />
              <span
                class="tpl:h-10 tpl:flex-1 tpl:rounded tpl:bg-[var(--tpl-bg-hover)]"
              />
              <span
                class="tpl:h-10 tpl:flex-1 tpl:rounded tpl:bg-[var(--tpl-bg-hover)]"
              />
            </span>
          </button>
        </div>
      </div> -->

      <!-- <div
        class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
      >
        <span class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]">{{
          t.popupDesign.rtl
        }}</span>
        <PopupDesignSwitch
          :checked="popup.design.rtl"
          @toggle="toggleDesign('rtl', !popup.design.rtl)"
        />
      </div> -->
    </template>

    <template v-else-if="designTab === 'position'">
      <p
        class="tpl:mb-3 tpl:text-[11px] tpl:font-semibold tpl:uppercase tpl:tracking-wide tpl:text-[var(--tpl-text-muted)]"
      >
        {{ t.popupDesign.positionHeading }}
      </p>
      <div
        class="tpl:mb-8 tpl:grid tpl:grid-cols-3 tpl:gap-2"
        role="group"
        :aria-label="t.popupDesign.positionHeading"
      >
        <button
          v-for="cell in POSITION_GRID"
          :key="cell.id"
          type="button"
          class="tpl:flex tpl:min-h-[52px] tpl:w-full tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-2 tpl:transition-all tpl:outline-none hover:tpl:border-[var(--tpl-text-muted)] focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
          :class="
            popup.design.popupPosition === cell.id
              ? 'tpl:border-2 tpl:border-[var(--tpl-text)] tpl:bg-[var(--tpl-bg-elevated)]'
              : ''
          "
          :aria-pressed="popup.design.popupPosition === cell.id"
          :aria-label="t.popupDesign.positionLabels[cell.id]"
          @click="patchDesign({ popupPosition: cell.id })"
        >
          <div :class="cell.cellAlign">
            <span
              class="tpl:block tpl:h-4 tpl:w-7 tpl:rounded-sm tpl:transition-colors"
              :class="
                popup.design.popupPosition === cell.id
                  ? 'tpl:bg-[var(--tpl-text)]'
                  : 'tpl:bg-[var(--tpl-text-muted)] tpl:opacity-70'
              "
            />
          </div>
        </button>
      </div>
      <p
        class="tpl:mb-2 tpl:text-[11px] tpl:font-semibold tpl:uppercase tpl:tracking-wide tpl:text-[var(--tpl-text-muted)]"
      >
        {{ t.popupDesign.displayAnimationHeading }}
      </p>
      <label class="tpl:sr-only" for="popup-display-animation">{{
        t.popupDesign.displayAnimationHeading
      }}</label>
      <select
        id="popup-display-animation"
        class="tpl:w-full tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2.5 tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
        :value="popup.design.displayAnimation"
        @change="onAnimationChange"
      >
        <option
          v-for="opt in animationSelectOptions"
          :key="opt.id"
          :value="opt.id"
        >
          {{ opt.label }}
        </option>
      </select>
    </template>

    <template v-else-if="designTab === 'closing'">
      <div
        class="tpl:space-y-5 tpl:rounded-[10px] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:px-4 tpl:py-5"
      >
        <div
          class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
        >
          <span
            class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
            >{{ t.popupDesign.closingDisplayCloseButton }}</span
          >
          <PopupDesignSwitch
            :checked="popup.design.displayCloseButton"
            @toggle="
              toggleDesign(
                'displayCloseButton',
                !popup.design.displayCloseButton,
              )
            "
          />
        </div>
        <div v-if="popup.design.displayCloseButton" class="tpl:space-y-2">
          <label
            class="tpl:block tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
            for="popup-close-button-position"
          >
            {{ t.popupDesign.closingButtonPosition }}
          </label>
          <select
            id="popup-close-button-position"
            class="tpl:w-full tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2.5 tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
            :value="popup.design.closeButtonPosition"
            @change="onCloseButtonPositionChange"
          >
            <option value="left">
              {{ t.popupDesign.closingButtonPositionLeft }}
            </option>
            <option value="right">
              {{ t.popupDesign.closingButtonPositionRight }}
            </option>
          </select>
        </div>
        <div
          class="tpl:flex tpl:min-h-[28px] tpl:items-center tpl:justify-between tpl:gap-4"
        >
          <span
            class="tpl:text-sm tpl:leading-snug tpl:text-[var(--tpl-text)]"
            >{{ t.popupDesign.closingOnOverlayClick }}</span
          >
          <PopupDesignSwitch
            :checked="popup.design.closeOnOverlayClick"
            @toggle="
              toggleDesign(
                'closeOnOverlayClick',
                !popup.design.closeOnOverlayClick,
              )
            "
          />
        </div>
      </div>
    </template>

    <template v-else>
      <p class="tpl:text-sm tpl:text-[var(--tpl-text-muted)]">
        {{ t.popupDesign.placeholderSubtab }}
      </p>
    </template>
  </div>
</template>
